/**
 * Contacts API Service
 * Contact management related API calls
 */

import { api } from '@/shared/api/apiClient';
import { API_ROUTES, buildUrl } from '@/config/apiRoutes';
import type { PaginatedResponse, SearchParams } from '@/shared/api/types';
import type { Contact, CreateContactDTO, UpdateContactDTO, ContactListParams, RelationshipType } from '@/types/entities';

// Re-export types for convenience
export type { Contact, CreateContactDTO, UpdateContactDTO, RelationshipType };

export interface ContactRelationship {
    id: number;
    contact_id: number;
    related_contact_id: number;
    relationship_type_id: number;
    created_at?: string;
    updated_at?: string;
}

export interface BatchRelationshipCreate {
    contact_id: number;
    related_contact_ids: number[];
    relationship_types: Record<string, number>;
    user_id?: number;
}

// Extended params with SearchParams
export interface ContactsListParams extends SearchParams, ContactListParams {}

/**
 * `GET /contacts` pages with `skip` / `limit` (default 100, max 1000), not
 * `page` / `per_page`; sending the latter silently returned the first 100
 * rows. Translate so callers can use either spelling.
 */
function toContactsQuery(params: ContactsListParams | undefined) {
    if (!params) return undefined;
    const { page, per_page, ...rest } = params;
    const limit = rest.limit ?? per_page;
    const skip = rest.skip ?? (page && limit ? (page - 1) * limit : undefined);
    // Spread into a plain object: buildUrl wants an indexable record,
    // which an interface type is not.
    return { ...rest, limit, skip };
}

// ===========================================
// Contacts Service
// ===========================================
export const contactsApi = {
    /**
     * Get paginated contacts list
     */
    getContacts: async (params?: ContactsListParams): Promise<PaginatedResponse<Contact>> => {
        const url = buildUrl(API_ROUTES.admin.contacts.base, toContactsQuery(params));
        return api.get<PaginatedResponse<Contact>>(url);
    },

    /**
     * Get single contact by ID
     */
    getContactById: async (id: number | string): Promise<Contact> => {
        return api.get<Contact>(API_ROUTES.admin.contacts.getById(id));
    },

    /**
     * Create new contact
     */
    createContact: async (data: CreateContactDTO): Promise<Contact> => {
        return api.post<Contact>(API_ROUTES.admin.contacts.create, data);
    },

    /**
     * Update existing contact (PATCH - partial update)
     */
    updateContact: async (id: number | string, data: UpdateContactDTO): Promise<Contact> => {
        return api.patch<Contact>(API_ROUTES.admin.contacts.update(id), data);
    },

    /**
     * Delete contact (soft delete)
     */
    deleteContact: async (id: number | string): Promise<void> => {
        return api.delete(API_ROUTES.admin.contacts.delete(id));
    },

    /**
     * Restore deleted contact
     */
    restoreContact: async (id: number | string): Promise<Contact> => {
        return api.post<Contact>(API_ROUTES.admin.contacts.restore(id));
    },

    /**
     * Ban contact
     */
    banContact: async (id: number | string): Promise<Contact> => {
        return api.post<Contact>(API_ROUTES.admin.contacts.ban(id));
    },

    /**
     * Unban contact
     */
    unbanContact: async (id: number | string): Promise<Contact> => {
        return api.post<Contact>(API_ROUTES.admin.contacts.unban(id));
    },

    /**
     * Get relationship types
     */
    getRelationshipTypes: async (): Promise<RelationshipType[]> => {
        return api.get<RelationshipType[]>(API_ROUTES.admin.relationshipTypes);
    },

    /**
     * Get relationships for a contact
     */
    getContactRelationships: async (contactId: number | string): Promise<ContactRelationship[]> => {
        return api.get<ContactRelationship[]>(API_ROUTES.admin.contacts.relationships(contactId));
    },

    /**
     * Create multiple contact relationships
     */
    createBatchRelationships: async (data: BatchRelationshipCreate): Promise<ContactRelationship[]> => {
        return api.post<ContactRelationship[]>(
            API_ROUTES.admin.contacts.relationshipsBatch(data.contact_id),
            data
        );
    },

    /**
     * Delete a contact relationship
     */
    deleteRelationship: async (
        contactId: number | string,
        relationshipId: number | string
    ): Promise<void> => {
        return api.delete(API_ROUTES.admin.contacts.relationship(contactId, relationshipId));
    },
};

export default contactsApi;
